/**
 * Krok PRU1 — pruvodce „Co si vyzkoušet" (`apps/api/src/guide/guide.ts`).
 *
 * Kroky se odskrtavaji na serveru podle SKUTECNE provedenych akci (audit).
 * Web jen cte stav, skryva/vraci kartu a z cilovych obrazovek hlasi dva kroky,
 * ktere nic nemeni (otevreny report, prohlednuty tip akvizice).
 */

import type { ApiPaths } from '@reos/api-client';
import { apiClient } from './api.js';
import { unwrap } from './api-error.js';

export type GuideDto = ApiPaths['/api/me/guide']['get']['responses']['200']['content']['application/json'];
export type GuideStepId = GuideDto['steps'][number]['id'];
export type GuideSeenStep =
  ApiPaths['/api/me/guide/seen']['post']['requestBody']['content']['application/json']['step'];

/** Klic dotazu — sdileny Dneskem, Nastavenim i menu profilu. */
export const GUIDE_QUERY_KEY = ['me', 'guide'] as const;

/**
 * Kam „Ukaž mi" vede. Zvyrazneni nese hash `#pruvodce-<krok>` (nektere
 * routy query parametry, ktere neznaji, zahazuji — hash zustane vzdy) a cil
 * oznaceny `data-guide="<krok>"` (viz `components/guide-highlight.tsx`).
 */
export function guideStepTarget(
  step: GuideStepId,
  context: { readonly caseId: string | null },
): string {
  const hash = `#pruvodce-${step}`;
  const caseId = context.caseId;
  switch (step) {
    case 'case':
      return `/zakazky/novy${hash}`;
    case 'report':
      return caseId === null ? `/zakazky${hash}` : `/zakazky/${caseId}?sekce=dokumenty${hash}`;
    case 'intake':
      return caseId === null ? `/nabor${hash}` : `/nabor?caseId=${caseId}${hash}`;
    case 'listing':
      return caseId === null ? `/inzerat${hash}` : `/zakazky/${caseId}/inzerat${hash}`;
    case 'acquisition':
      return `/akvizice${hash}`;
    case 'contract':
      return caseId === null ? `/smlouvy/nova${hash}` : `/smlouvy/nova?zakazka=${caseId}${hash}`;
    case 'mailbox':
      return `/nastaveni${hash}`;
  }
}

export async function getGuide(): Promise<GuideDto> {
  return unwrap(apiClient.GET('/api/me/guide'));
}

export async function setGuideHidden(hidden: boolean): Promise<GuideDto> {
  return unwrap(apiClient.PATCH('/api/me/guide', { body: { hidden } }));
}

/**
 * Cilova obrazovka hlasi krok bez mutace. Selhani NIKDY nerozbije obrazovku,
 * ktera ho hlasi — pruvodce je napoveda, ne soucast prace.
 */
export async function markGuideSeen(step: GuideSeenStep): Promise<void> {
  try {
    await apiClient.POST('/api/me/guide/seen', { body: { step } });
  } catch {
    // Zamerne tichy: report i tipy funguji dal; krok se odskrtne priste.
  }
}

export async function createDemoCase(): Promise<{ caseId: string }> {
  return unwrap(apiClient.POST('/api/me/guide/demo'));
}

export async function removeDemoCases(): Promise<{ removed: number }> {
  return unwrap(apiClient.DELETE('/api/me/guide/demo'));
}
